import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { fetchAccount, openBillingPortal, type BillingAccount } from '../services/billing';
import { PageHeader } from '../components/PageHeader';
import { BRAND } from '../brand';

const RowIcon = ({ children }: { children: React.ReactNode }) => (
  <span className="list-row-icon" aria-hidden>{children}</span>
);

const Chevron = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="m9 5 7 7-7 7" />
  </svg>
);

export const SettingsPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [account, setAccount] = useState<BillingAccount | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!user) return;
    try {
      setAccount(await fetchAccount(user));
    } catch {
      /* 账户信息拿不到不影响其他设置项 */
    }
  }, [user]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const manageSubscription = async () => {
    if (!user || busy) return;
    setBusy(true);
    try {
      const url = await openBillingPortal(user);
      window.location.href = url;
    } catch {
      setBusy(false);
    }
  };

  const name = user?.displayName || user?.email?.split('@')[0] || '观象用户';
  const initial = name.charAt(0).toUpperCase();
  const isBasic = account?.plan === 'basic';
  const balance = account?.balance ?? null;
  const quota = account?.monthlyCredits || 0;
  const progress = quota > 0 && balance !== null ? Math.max(0, Math.min(100, (balance / quota) * 100)) : 0;
  const periodEnd = account?.periodEnd
    ? new Date(account.periodEnd).toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  return (
    <div className="page">
      <PageHeader eyebrow="账户" title="我的" subtitle="管理订阅与额度，查看版本信息。" />

      <section className="profile-card">
        <span className="profile-avatar">{initial}</span>
        <div className="profile-text">
          <h2 className="profile-name">{name}</h2>
          <p className="profile-email">{user?.email || '未登录'}</p>
        </div>
        <span className={`plan-badge ${isBasic ? 'is-basic' : ''}`}>{account?.planName || '免费'}</span>
      </section>

      <section className="plan-card">
        <div className="plan-head">
          <div>
            <div className="field-label">AI 解读额度</div>
            <div className="plan-value">
              <b>{balance ?? '—'}</b>
              <span>次</span>
            </div>
          </div>
          <button className="text-btn" onClick={() => navigate('/app/pricing')}>
            {isBasic ? '查看权益' : '升级基础版'}
          </button>
        </div>

        {quota > 0 && balance !== null && (
          <div className="plan-bar" aria-hidden>
            <span style={{ width: `${progress}%` }} />
          </div>
        )}

        <div className="plan-meta">
          <span>{isBasic ? '基础版 · 每月自动发放' : '免费版 · 注册赠送额度'}</span>
          {periodEnd && <span>本期至 {periodEnd}</span>}
        </div>

        {isBasic && (
          <button className="mei-ghost-btn plan-manage" onClick={manageSubscription} disabled={busy}>
            {busy ? '打开中…' : '管理订阅 / 取消'}
          </button>
        )}
      </section>

      <section className="list-card">
        <button className="list-row" onClick={() => navigate('/app/history')}>
          <RowIcon>
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" />
            </svg>
          </RowIcon>
          <span className="list-row-label">求测记录</span>
          <span className="list-row-value">梅花 · 奇门 · 八字</span>
          <Chevron />
        </button>

        <button className="list-row" onClick={() => navigate('/app/pricing')}>
          <RowIcon>
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="18" height="14" rx="3" /><path d="M3 10h18" />
            </svg>
          </RowIcon>
          <span className="list-row-label">订阅与额度</span>
          <span className="list-row-value">{account?.planName || '免费'}</span>
          <Chevron />
        </button>

        <div className="list-row list-row-static">
          <RowIcon>
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" />
            </svg>
          </RowIcon>
          <span className="list-row-label">版本</span>
          <span className="list-row-value">{BRAND.version} Web</span>
        </div>
      </section>

      <details className="about-card">
        <summary>
          <span>关于 {BRAND.name}</span>
          <span className="about-chevron">展开</span>
        </summary>
        <div className="about-body">
          <p>{BRAND.name}（{BRAND.english}）是一个把梅花易数、奇门遁甲与八字排盘放在同一套体验里的 AI 命理工具。排盘由程序算定，AI 负责按术数次第解释依据。</p>
          <p className="about-muted">所有解读仅供传统文化研究与个人参考，不构成医疗、法律、投资或人生决策建议。</p>
        </div>
      </details>

      <button className="signout-btn" onClick={() => void logout()}>
        退出登录
      </button>

      <div className="settings-foot">{BRAND.tagline} · {BRAND.descriptor}</div>
    </div>
  );
};
